<script>
import Title from "@/components/admin/common/TitleArea.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import PartnerFulfillmentGuide from "@/components/admin/fulfillment/guide/PartnerFulfillmentGuide.vue"

export default {
  name: 'PartnerSkuProductDetail',
  components: {
    Title,
    StatusBadge,
    PageGuideDialog,
    PartnerFulfillmentGuide
  },
  data() {
    return {
      description: {
        title: '내 상품 상세'
      },
      partnerSkuCode: '',
      detail: null,
      loading: false,
      guideDialog: false,
    }
  },
  computed: {
    isMapped() {
      return this.detail?.detail_type === 'MAPPED'
    },
    requestTypeLabel() {
      if (this.detail?.request_type === 'LINK_REQUEST') return '연결요청'
      if (this.detail?.request_type === 'NEW_REQUEST') return '신규등록요청'
      return '정상 연결'
    },
    statusBadge() {
      return this.isMapped ? 'MAPPED' : this.detail?.status
    },
    updatedAt() {
      return this.detail?.updated_at || this.detail?.created_at
    },
    specRows() {
      const spec = this.detail?.spec_detail || {}
      switch (this.detail?.spec_type) {
        case 'DIMENSION':
          return [
            { label: '가로', value: this.withUnit(spec.length, 'cm') },
            { label: '세로', value: this.withUnit(spec.width, 'cm') },
            { label: '높이', value: this.withUnit(spec.height, 'cm') },
          ]
        case 'VOLUME':
          return [
            { label: '부피', value: this.withUnit(spec.value, spec.unit) },
          ]
        case 'APPAREL_SIZE':
          return [
            { label: '의류 사이즈', value: this.formatValue(spec.size) },
          ]
        case 'PRODUCT_OPTION':
          return [
            { label: '모델코드', value: this.formatValue(spec.model_code) },
            { label: '색상', value: this.formatValue(spec.color) },
            { label: '사이즈', value: this.formatValue(spec.size) },
          ]
        case 'PACK_COUNT':
          return [
            { label: '포장 단위', value: this.formatValue(spec.unit) },
            { label: '입수량', value: this.formatValue(spec.count) },
            { label: '표기 단위', value: this.formatValue(spec.pack_uom) },
          ]
        default:
          return []
      }
    }
  },
  methods: {
    formatValue(value) {
      return value === null || value === undefined || value === '' ? '-' : value
    },
    withUnit(value, unit) {
      const normalized = this.formatValue(value)
      return normalized === '-' ? '-' : `${normalized}${unit ? ` ${unit}` : ''}`
    },
    formatDateTime(value) {
      return value ? this.$formatDate(value, 'YYYY-MM-DD HH:mm:ss') : '-'
    },
    goToList() {
      let query = {}
      try {
        query = JSON.parse(String(this.$route.query?.returnQuery || '{}'))
      } catch (_) {
        query = {}
      }
      this.$router.push({ path: '/admin/fulfillment/sku-requests', query })
    },
    async loadDetail() {
      if (!this.partnerSkuCode) return
      this.loading = true
      const query = new URLSearchParams({
        partner_sku_code: this.partnerSkuCode
      }).toString()
      const config = {
        method: 'GET',
        url: `/api/fulfillment/partner/sku-products/detail?${query}`,
        name: '[ Fulfillment / Partner 내 상품 상세 ]'
      }
      try {
        const res = await this.$http(config)
        this.detail = res.data?.data || null
      } catch (_) {
        this.detail = null
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.partnerSkuCode = String(this.$route.query?.partner_sku_code || '').trim()
    this.loadDetail()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="내 상품 상세 사용 가이드" @click="guideDialog = true">
              <q-tooltip>내 상품 상세 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn flat color="grey-7" icon="arrow_back" label="목록으로" @click="goToList" />
        </Title>
        <div class="text-caption text-grey-7 q-mt-sm">
          내 SKU의 연결 상태와 운영팀 처리 결과를 확인합니다.
        </div>
      </div>
    </div>

    <q-inner-loading v-if="loading" :showing="true">
      <q-spinner color="primary" size="42px" />
    </q-inner-loading>

    <q-banner v-if="!loading && !detail" rounded class="bg-grey-2 text-grey-8">
      상품 상세 정보를 찾을 수 없습니다. 목록에서 다시 선택해 주세요.
    </q-banner>

    <template v-if="detail">
      <div class="row q-col-gutter-md q-mb-lg items-stretch">
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="comp-box q-pa-md summary-card">
            <div class="text-caption text-grey">내 SKU</div>
            <div class="text-subtitle1 text-weight-bold q-mt-sm">{{ detail.partner_sku_code }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="comp-box q-pa-md summary-card">
            <div class="text-caption text-grey">처리 상태</div>
            <div class="q-mt-sm"><StatusBadge :status="statusBadge" /></div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="comp-box q-pa-md summary-card">
            <div class="text-caption text-grey">요청 유형</div>
            <div class="text-subtitle1 text-weight-bold q-mt-sm">{{ requestTypeLabel }}</div>
          </div>
        </div>
        <div class="col-12 col-sm-6 col-lg-3">
          <div class="comp-box q-pa-md summary-card">
            <div class="text-caption text-grey">최근 변경일</div>
            <div class="text-body2 text-weight-medium q-mt-sm">{{ formatDateTime(updatedAt) }}</div>
          </div>
        </div>
      </div>

      <div class="row q-col-gutter-md items-stretch">
        <div class="col-12 col-lg-7">
          <div class="comp-box q-pa-lg detail-card">
            <div class="text-subtitle1 text-weight-bold q-mb-md">상품 정보</div>
            <div class="detail-grid">
              <div><span>상품명</span><b>{{ formatValue(detail.display_name || detail.partner_display_name) }}</b></div>
              <div><span>기준 상품 코드</span><b>{{ formatValue(detail.master_sku_code) }}</b></div>
              <div><span>바코드번호</span><b>{{ formatValue(detail.barcode) }}</b></div>
              <div><span>브랜드</span><b>{{ formatValue(detail.brand_name) }}</b></div>
              <div><span>카테고리</span><b>{{ formatValue(detail.category_name) }}</b></div>
              <div><span>HS CODE</span><b>{{ formatValue(detail.hs_code) }}</b></div>
              <div><span>규격 타입</span><b>{{ formatValue(detail.spec_type) }}</b></div>
              <div><span>규격 키</span><b>{{ formatValue(detail.spec_key) }}</b></div>
              <div><span>요청 ID</span><b>{{ formatValue(detail.request_id) }}</b></div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-5">
          <div class="comp-box q-pa-lg detail-card">
            <div class="text-subtitle1 text-weight-bold q-mb-md">규격 상세</div>
            <div v-if="specRows.length" class="detail-grid detail-grid--single">
              <div v-for="row in specRows" :key="row.label">
                <span>{{ row.label }}</span>
                <b>{{ row.value }}</b>
              </div>
            </div>
            <div v-else class="text-caption text-grey-7">등록된 규격 상세 정보가 없습니다.</div>
          </div>
        </div>
      </div>

      <q-banner v-if="detail.status === 'REJECTED'" rounded class="bg-red-1 text-red-9 q-mt-md">
        <template #avatar><q-icon name="error_outline" /></template>
        <b>반려 사유</b>
        <div class="q-mt-xs">{{ formatValue(detail.reject_reason) }}</div>
      </q-banner>

      <q-banner v-else rounded class="bg-blue-1 text-blue-9 q-mt-md">
        <template #avatar><q-icon name="info_outline" /></template>
        {{ isMapped ? '내 SKU가 기준 상품과 정상적으로 연결되어 있습니다.' : '운영팀이 요청 내용을 검토하고 있습니다.' }}
      </q-banner>
    </template>

    <PageGuideDialog
      v-model="guideDialog"
      title="내 상품 상세 사용 가이드"
      description="상품 연결 상태와 요청 처리 결과를 확인하는 방법입니다."
    >
      <PartnerFulfillmentGuide guide-key="productDetail" />
    </PageGuideDialog>
  </q-page>
</template>

<style scoped lang="scss">
.summary-card,
.detail-card {
  height: 100%;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px 24px;
}

.detail-grid--single {
  grid-template-columns: 1fr;
}

.detail-grid span,
.detail-grid b {
  display: block;
}

.detail-grid span {
  margin-bottom: 4px;
  color: #7a8989;
  font-size: 12px;
}

.detail-grid b {
  color: #183b56;
  word-break: break-all;
}

@media (max-width: 600px) {
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
